/* eslint-disable max-len */
import AdjustDeductible from '../components/AdjustCoverage/AdjustDeductible';

import type { ElementType } from 'react';
import type { AdjustCoverageAction, AdjustCoverageInformationProps } from '../types';
import type {
    EditTermPriceDTO,
    OPCTermDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/opc';
import { type DSSPaymentFrequency } from 'gw-edge-client/dist/types/dto/pc/typekey';
import {
    getCurrencyString,
    paymentFrequencyMap,
    periodicityUnit
} from '../../../utils/currencyUtil';
import AdjustInjuryDegree from '../components/AdjustCoverage/AdjustInjuryDegree';
import AdjustInsuranceSum from '../components/AdjustCoverage/AdjustInsuranceSum';
import AdjustAudioTelco from '../components/AdjustCoverage/AdjustAudioTelco';
import { formatMonetaryAmountToStringWithoutCurrency } from '../../../utils/coverablesUtil';
import AdjustPrhHolidayInsuranceSum from '../components/AdjustCoverage/AdjustPrhHolidayInsuranceSum';
import type { AmountDTO } from 'gw-edge-client/dist/types/dto/pc/edge/capabilities/currency/dto';
import AdjustUvpContentSum from '../components/AdjustCoverage/AdjustUvpContentSum';

interface AdjustCoverage {
    title: string;
    AdjustComponent: ElementType<{
        termInfo: OPCTermDTO;
        updateTermValue: (termValue: number) => void;
        updateTermStringValue: (termValue: string) => void;
        isAdjustableDataValid: boolean;
    }>;
    adjustInformationProps?: AdjustCoverageInformationProps;
}

const getFormattedTerms = (
    originalTermValue: number | string,
    newTermValue: AmountDTO | undefined,
    unitValue: string
): { oldTermDisplay: string; newTermDisplay: string } => {
    if (typeof originalTermValue === 'number') {
        // Default behavior
        const oldTermDisplay = `${formatMonetaryAmountToStringWithoutCurrency(originalTermValue)} ${unitValue}`;
        const newTermDisplay = `${formatMonetaryAmountToStringWithoutCurrency(newTermValue?.amount ?? 0)} ${unitValue}`;
        return {
            oldTermDisplay,
            newTermDisplay
        };
    } else if (typeof originalTermValue === 'string') {
        // currently only support OPC - PRH Holiday Home
        const oldTermDisplay = originalTermValue;
        const newTermDisplay = newTermValue?.amountString ?? '0';
        return {
            oldTermDisplay,
            newTermDisplay
        };
    }
    throw new Error('Add implementation for your lob');
};

const getCommonAdjustProps = (
    originalTermValue: number | string,
    editTermPriceDTO: EditTermPriceDTO,
    unitValue: string,
    termName: string,
    selectedPaymentFrequency?: DSSPaymentFrequency
): AdjustCoverageInformationProps => {
    const oldPriceLabel = 'DSS-COVERABLE-DETAILS-PRICE-AFFECT-BEFORE';
    const newPriceLabel = 'DSS-COVERABLE-DETAILS-PRICE-AFFECT-AFTER';

    const currencyPostfix =
        periodicityUnit[selectedPaymentFrequency ?? paymentFrequencyMap.monthly];
    const title = undefined;
    const { newPrice, oldPrice, newTermValue } = editTermPriceDTO;
    const isMonthly = selectedPaymentFrequency === paymentFrequencyMap.monthly;
    const oldPriceAmount = isMonthly ? oldPrice?.monthlyTotal.amount : oldPrice?.yearlyTotal.amount;
    const newPriceAmount = isMonthly ? newPrice?.monthlyTotal.amount : newPrice?.yearlyTotal.amount;
    const oldPriceDisplay = getCurrencyString(oldPriceAmount ?? 0, ` kr.${currencyPostfix}`, 2);
    const newPriceDisplay = getCurrencyString(newPriceAmount ?? 0, ` kr.${currencyPostfix}`, 2);
    const { oldTermDisplay, newTermDisplay } = getFormattedTerms(
        originalTermValue,
        newTermValue,
        unitValue
    );
    const mobileProps = {
        before: {
            price: {
                label: oldPriceLabel,
                value: oldPriceDisplay
            },
            attribute: {
                label: termName,
                value: oldTermDisplay
            }
        },
        after: {
            price: {
                label: oldPriceLabel,
                value: newPriceDisplay
            },
            attribute: {
                label: termName,
                value: newTermDisplay
            }
        }
    };

    const desktopProps = {
        before: {
            priceLable: oldPriceLabel,
            priceValue: `${isMonthly ? 'Månedlig' : 'Årligt'} pris: ${oldPriceDisplay}`,
            attributeValue: `${termName}: ${oldTermDisplay}`
        },
        after: {
            priceLable: newPriceLabel,
            priceValue: `${isMonthly ? 'Månedlig' : 'Årligt'}  pris: ${newPriceDisplay}`,
            attributeValue: `${termName}: ${newTermDisplay}`
        }
    };

    const detailPriceText = 'DSS-COVERABLE-DETAILS-ADAPT-DEDUCTIBLE-DETAIL';
    return { title, mobileProps, desktopProps, detailPriceText };
};

const getAdjustDeductibleProps = (
    originalTermValue: number | string,
    editTermPriceDTO: EditTermPriceDTO,
    selectedPaymentFrequency?: DSSPaymentFrequency
): AdjustCoverageInformationProps => {
    return getCommonAdjustProps(
        originalTermValue,
        editTermPriceDTO,
        'kr.',
        'Selvrisiko',
        selectedPaymentFrequency
    );
};

const getAdjustInjuryDegreeProps = (
    originalTermValue: number,
    editTermPriceDTO: EditTermPriceDTO,
    selectedPaymentFrequency?: DSSPaymentFrequency
): AdjustCoverageInformationProps => {
    return getCommonAdjustProps(
        originalTermValue,
        editTermPriceDTO,
        '%',
        'Invalidesum',
        selectedPaymentFrequency
    );
};

const getAdjustSumInsuredProps = (
    originalTermValue: number | string,
    editTermPriceDTO: EditTermPriceDTO,
    selectedPaymentFrequency?: DSSPaymentFrequency
): AdjustCoverageInformationProps => {
    return getCommonAdjustProps(
        originalTermValue,
        editTermPriceDTO,
        'kr.',
        'Forsikringssum',
        selectedPaymentFrequency
    );
};

const getAdjustCoverageConfig = (
    action: AdjustCoverageAction,
    editTermPriceDTO: EditTermPriceDTO,
    originalTermValue: number,
    originalTermStringValue: string,
    selectedPaymentFrequency?: DSSPaymentFrequency
): AdjustCoverage => {
    switch (action) {
        case 'Deductible':
            return {
                title: 'DSS-COVERABLE-DETAILS-ADAPT-DEDUCTIBLE',
                AdjustComponent: AdjustDeductible,
                adjustInformationProps: getAdjustDeductibleProps(
                    originalTermValue,
                    editTermPriceDTO,
                    selectedPaymentFrequency
                )
            };
        case 'AudioTelcoDropdown':
            return {
                title: 'DSS-COVERABLE-DETAILS-ADAPT-SUM-HEADER',
                AdjustComponent: AdjustAudioTelco,
                adjustInformationProps: getCommonAdjustProps(
                    originalTermValue,
                    editTermPriceDTO,
                    'kr.',
                    'Sum',
                    selectedPaymentFrequency
                )
            };
        case 'InjuryDegree':
            return {
                title: 'DSS-COVERABLE-DETAILS-INJURY-DEGREE-HEADER',
                AdjustComponent: AdjustInjuryDegree,
                adjustInformationProps: getAdjustInjuryDegreeProps(
                    originalTermValue,
                    editTermPriceDTO,
                    selectedPaymentFrequency
                )
            };
        case 'Limit':
            return {
                title: 'DSS-COVERABLE-DETAILS-ADAPT-SUM-HEADER',
                AdjustComponent: AdjustInsuranceSum,
                adjustInformationProps: getAdjustSumInsuredProps(
                    originalTermValue,
                    editTermPriceDTO,
                    selectedPaymentFrequency
                )
            };
        case 'PRHPrivateContentSum':
            return {
                title: 'DSS-COVERABLE-DETAILS-ADAPT-SUM-HEADER',
                AdjustComponent: AdjustPrhHolidayInsuranceSum,
                adjustInformationProps: getAdjustSumInsuredProps(
                    originalTermStringValue,
                    editTermPriceDTO,
                    selectedPaymentFrequency
                )
            };
        case 'UVPContentSum':
            return {
                title: 'DSS-COVERABLE-DETAILS-ADAPT-SUM-HEADER',
                AdjustComponent: AdjustUvpContentSum,
                adjustInformationProps: getAdjustSumInsuredProps(
                    originalTermValue,
                    editTermPriceDTO,
                    selectedPaymentFrequency
                )
            };
        default:
            throw new Error(`Unsupported for action: ${String(action)}`);
    }
};

export default getAdjustCoverageConfig;
